import { Linkable } from '@/components/blocks/Linkable';
import { MuiCardMedia } from '@/components/blocks/MuiCardMedia';
import { productCardSX } from '@/components/blocks/ProductCard/styles/card';
import { productCardContentSX } from '@/components/blocks/ProductCard/styles/cardContent';
import {
	productCardMediaSX,
	productCardRibbonSX,
} from '@/components/blocks/ProductCard/styles/cardMedia';
import { productCardDetailsSX } from '@/components/blocks/ProductCard/styles/details';
import { productCardNameSX } from '@/components/blocks/ProductCard/styles/name';
import { useProductEvents } from '@/data/Content/_ProductEvents';
import { useLocalization } from '@/data/Localization';
import { RibbonImage } from '@/data/types/Product';
import { Box, Card, CardContent, Stack, SxProps, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import { Img } from '@/components/blocks/MaterialImage';
import { productCardPriceSX } from '@/components/blocks/ProductCard/styles/price';
import { combineSX } from '@/utils/combineSX';
import { ProductCardPrice } from '@/components/blocks//ProductCard/Price';
import NextHead from 'next/head';
import { HelloretailProductType } from '@/data/types/HelloRetail';

export const ProductCard: FC<{
	product: HelloretailProductType;
	clickAction?: (trackingCode: string) => void;
	styles?: { [key: string]: SxProps };
	imgLoading?: 'eager' | 'lazy';
}> = ({ product, clickAction, styles, imgLoading = 'lazy' }) => {
	const eventProps = useMemo(() => ({ product }), [product]);
	const priceDisplayNLS = useLocalization('PriceDisplay');
	const eaaMsgsNLS = useLocalization('EaaMessages');
	const { onClick } = useProductEvents(eventProps);
	const routeUrl = useMemo(() => product?.seo?.href as string, [product]);
	const helloRetailCard = 1;
	const ribbonData: RibbonImage = product?.ribbonImages //Todo Is ribbons possible ?
		? product?.ribbonImages[0]
		: { ribbonName: '', ribbonPath: '' };

	return (
		<Card
			onClick={onClick(()=> clickAction && clickAction(product.trackingCode))}
			sx={productCardSX(({helloRetailCard}))}
			id={product.partNumber}
			data-testid={product.partNumber}
			component={Linkable}
			href={routeUrl}
			aria-label={eaaMsgsNLS.productLinkPrefix.t()+" "+product.name}
		>
			{imgLoading === 'eager' ? (
				<NextHead>
					<link rel="preload" href={product.thumbnail} />
				</NextHead>
			) : null}
			<Box sx={productCardDetailsSX}>
					<Box>
						{ribbonData?.ribbonPath ? (
							<Box sx={productCardRibbonSX}>
								<Img
									fetchPriority="high"
									loading={imgLoading}
									decoding="async"
									src={ribbonData.ribbonPath}
									alt={ribbonData.ribbonName}
								></Img>
							</Box>
						) : null}
						<MuiCardMedia
							sx={combineSX([productCardMediaSX, styles?.cardImageSx ? styles.cardImageSx : {}, {height:'auto'}])}
							component="img"
							image={product.thumbnail}
							alt={product.name}
							fetchPriority="high"
							loading={imgLoading}
							decoding="async"
							height="200"
							width="200"
							/* onError={onMediaFallback} */
						></MuiCardMedia>
					</Box>
				<CardContent sx={productCardContentSX}>
					<Stack spacing={1}>
						<Typography
							variant="h3"
							align="center"
							id={`${product.partNumber}-name`}
							data-testid={`${product.partNumber}-name`}
							sx={combineSX([productCardNameSX, styles?.cardNameSx ?? styles?.cardNameSx])}
						>
							{product.name}
						</Typography>
						{product?.productPrice?.min ? (
							<ProductCardPrice price={product.productPrice} />
						) : (
							<Typography variant="body1" align="center" sx={productCardPriceSX}>
								{priceDisplayNLS.Labels.Pending.t()}
							</Typography>
						)}
					</Stack>
				</CardContent>
			</Box>
		</Card>
	);
};
